import { useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, Tooltip, Sector } from 'recharts';
import { useTheme } from '@mui/material';

import { componentsSize } from 'constants/components';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { SingleValueTooltip } from 'components/charts/SingleValueTooltip';

const defaultTooltipPosition = { x: 'auto', y: 'auto' };

export const CustomPieChart = ({ data = [], size = componentsSize.medium }) => {
  const [activeIndex, setActive] = useState(null);
  const [position, setPosition] = useState(defaultTooltipPosition);

  const { palette } = useTheme();
  const { isMdUp: isMdWidth, isLgUp: isLgWidth, getResponsiveTypography } = useBreakpointFlags();

  const getTooltipPosition = (currentTooltipPosition = {}) => {
    const { x: defaultXPosition = 0, y: defaultYPosition = 0 } = currentTooltipPosition;
    let tooltipWidth = 100;

    if (isMdWidth) {
      tooltipWidth = 150;
    }

    if (isLgWidth) {
      tooltipWidth = 250;
    }

    const x = defaultXPosition - tooltipWidth / 2;
    const y = defaultYPosition - 100;

    return { ...currentTooltipPosition, x, y };
  };

  const onPieEnter = (chartData, index) => {
    setActive(index);
    setPosition(getTooltipPosition(chartData?.tooltipPosition));
  };

  const onTouchStart = (_chartData, _index, event) => {
    event.preventDefault();
  };

  const onPieLeave = () => {
    setActive(null);
    setPosition(defaultTooltipPosition);
  };

  const reducer = (accumulator, currentValue) => accumulator + currentValue.value;

  const styles = {
    title: {
      ...getResponsiveTypography('h2-bold'),
      fill: palette.text.primary,
    },
    subTitle: {
      ...getResponsiveTypography('button'),
      fill: palette.text.primary,
    },
  };

  return (
    <PieChart width={size === componentsSize.small ? 190 : 270} height={size === componentsSize.small ? 190 : 270}>
      <Pie
        activeIndex={activeIndex}
        activeShape={(props) => {
          const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
          const radiusIncrement = isMdWidth ? 15 : 7;

          return (
            <g>
              <text x="50%" y="50%" textAnchor="middle" style={styles.title}>
                {payload?.value || ''}
              </text>
              <text x="50%" y="57%" textAnchor="middle" style={styles.subTitle}>
                surveys
              </text>

              <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + radiusIncrement}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
              />
            </g>
          );
        }}
        data={data}
        cx="50%"
        cy="50%"
        innerRadius="40%"
        outerRadius="100%"
        fill="#8884d8"
        paddingAngle={0}
        dataKey="value"
        stroke="none"
        onMouseEnter={onPieEnter}
        onMouseLeave={onPieLeave}
        onTouchStart={onTouchStart}
      >
        {data.map((entry, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Cell key={`cell-${index}`} fill={entry?.color} />
        ))}
      </Pie>
      <Tooltip content={<SingleValueTooltip />} position={position} />
      {activeIndex === null && (
        <g>
          <text x="50%" y="50%" textAnchor="middle" style={styles.title}>
            {data.reduce(reducer, 0)}
          </text>
          <text x="50%" y="57%" textAnchor="middle" style={styles.subTitle}>
            surveys
          </text>
        </g>
      )}
    </PieChart>
  );
};

CustomPieChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  size: PropTypes.oneOf(Object.values(componentsSize)),
};
